import type {
	NotifiableError,
	OnErrorCallback,
} from '@bugsnag/js';

export type ErrorSeverity = 'info' | 'warning' | 'error';

/**
 * Report an error to Bugsnag when the client is available.
 *
 * Replaces the repeated `if (typeof window.glBugsnagClient !== 'undefined')`
 * guard + `notify(...)` boilerplate scattered across the codebase. The client
 * is only present once `configureBugsnag` has run, so callers that may fire
 * before/around launch must not assume it exists — this no-ops when it does
 * not.
 *
 * @param error Error (or notifiable value) to report.
 * @param severity Bugsnag severity to stamp on the event. Defaults to `warning`.
 * @param onError Optional callback for extra event shaping (e.g. `addMetadata`).
 *   Runs after the severity is applied; its return value is forwarded to Bugsnag.
 */
export default function reportError(
	error: unknown,
	severity: ErrorSeverity = 'warning',
	onError?: OnErrorCallback,
): void {
	/*
	 * Guard `window` itself first: `typeof window.glBugsnagClient` throws a
	 * ReferenceError in non-browser contexts (node scripts, SSR, node-env tests)
	 * where `window` is undefined.
	 */
	if (typeof window === 'undefined' || typeof window.glBugsnagClient === 'undefined') {
		return;
	}

	window.glBugsnagClient.notify(
		error as NotifiableError,
		(
			event,
			cb,
		) => {
			event.severity = severity;

			if (onError) {
				return onError(
					event,
					cb,
				);
			}

			return true;
		},
	);
}
